import { useState } from "react";
import toast from "react-hot-toast";
import { X, Plus, Trash2 } from "lucide-react";
import { useCreateJournalEntry, useChartOfAccounts, JournalEntryLinePayload } from "@/api/accounting";
import { useClients, displayName } from "@/api/clients";
import { getApiErrorMessage } from "@/api/client";

interface LineDraft {
  chartOfAccountId: string;
  debit: string;
  credit: string;
  description: string;
}

const emptyLine = (): LineDraft => ({ chartOfAccountId: "", debit: "", credit: "", description: "" });

export default function CreateJournalEntryModal({
  clientId: fixedClientId,
  onClose,
}: {
  clientId?: string;
  onClose: () => void;
}) {
  const createEntry = useCreateJournalEntry();
  const { data: accountOptions } = useChartOfAccounts({ pageSize: 200 });
  const [entryDate, setEntryDate] = useState("");
  const [description, setDescription] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([emptyLine(), emptyLine()]);
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState<{ id: string; name: string } | null>(null);

  const { data: clientResults } = useClients({ search: clientSearch, pageSize: 6 });
  const effectiveClientId = fixedClientId ?? selectedClient?.id;

  const totalDebit = lines.reduce((sum, l) => sum + Number(l.debit || 0), 0);
  const totalCredit = lines.reduce((sum, l) => sum + Number(l.credit || 0), 0);
  const isBalanced = totalDebit === totalCredit && totalDebit > 0;

  const updateLine = (index: number, patch: Partial<LineDraft>) => {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isBalanced) {
      toast.error("El asiento debe balancear: la suma de débitos debe ser igual a la suma de créditos.");
      return;
    }
    const payloadLines: JournalEntryLinePayload[] = lines
      .filter((l) => l.chartOfAccountId)
      .map((l) => ({
        chartOfAccountId: l.chartOfAccountId,
        debit: l.debit || undefined,
        credit: l.credit || undefined,
        description: l.description || undefined,
      }));
    try {
      await createEntry.mutateAsync({
        clientId: effectiveClientId || undefined,
        entryDate,
        description,
        lines: payloadLines,
      });
      toast.success("Asiento contable creado correctamente.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="card w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Nuevo asiento contable</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
          {!fixedClientId && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Cliente (opcional)</label>
              {selectedClient ? (
                <div className="mt-1 flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm">
                  <span>{selectedClient.name}</span>
                  <button type="button" className="text-xs text-gray-400 hover:text-red-600" onClick={() => setSelectedClient(null)}>
                    Quitar
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    className="input-field mt-1"
                    placeholder="Buscar cliente (déjelo vacío para un asiento interno)..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                  />
                  {clientSearch && clientResults?.data && clientResults.data.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                      {clientResults.data.map((c) => (
                        <button
                          type="button"
                          key={c.id}
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                          onClick={() => {
                            setSelectedClient({ id: c.id, name: displayName(c) });
                            setClientSearch("");
                          }}
                        >
                          {displayName(c)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Fecha</label>
              <input type="date" className="input-field mt-1" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Descripción</label>
              <input className="input-field mt-1" value={description} onChange={(e) => setDescription(e.target.value)} required />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">Líneas (débito / crédito)</label>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-800"
                onClick={() => setLines((prev) => [...prev, emptyLine()])}
              >
                <Plus size={12} /> Agregar línea
              </button>
            </div>
            <div className="mt-2 space-y-2">
              {lines.map((line, index) => (
                <div key={index} className="flex items-center gap-2 rounded-md border border-gray-100 p-2">
                  <select
                    className="input-field flex-1 text-xs"
                    value={line.chartOfAccountId}
                    onChange={(e) => updateLine(index, { chartOfAccountId: e.target.value })}
                    required
                  >
                    <option value="">Cuenta...</option>
                    {accountOptions?.data.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} — {a.name}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min="0"
                    placeholder="Débito"
                    className="input-field w-28 text-xs"
                    value={line.debit}
                    onChange={(e) => updateLine(index, { debit: e.target.value, credit: e.target.value ? "" : line.credit })}
                  />
                  <input
                    type="number"
                    min="0"
                    placeholder="Crédito"
                    className="input-field w-28 text-xs"
                    value={line.credit}
                    onChange={(e) => updateLine(index, { credit: e.target.value, debit: e.target.value ? "" : line.debit })}
                  />
                  {lines.length > 2 && (
                    <button
                      type="button"
                      className="text-gray-400 hover:text-red-600"
                      onClick={() => setLines((prev) => prev.filter((_, i) => i !== index))}
                      aria-label="Quitar línea"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className={`mt-2 flex justify-end gap-4 text-xs font-medium ${isBalanced ? "text-green-600" : "text-red-600"}`}>
              <span>Débitos: {totalDebit.toLocaleString("es-CO")}</span>
              <span>Créditos: {totalCredit.toLocaleString("es-CO")}</span>
              <span>{isBalanced ? "Balanceado" : "No balancea"}</span>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={createEntry.isPending || !isBalanced}>
              {createEntry.isPending ? "Creando..." : "Crear asiento"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
